import axios from "axios";
import React from "react";
import { fetchAxios } from "../API/fetchAxios";
import { useToast } from "./toast";
import { useValidateErrorResponse } from "./useValidateErrorResponse";

const { CancelToken } = axios;

const defaultMethods = {
  GET: null,
  POST: null,
  PUT: null,
  PATCH: null,
  DELETE: null,
};

const AXIOS_RESPONSE_TYPES = new Set([
  "arraybuffer",
  "blob",
  "document",
  "json",
  "text",
  "stream",
]);

const resolveAxiosResponseType = (value) => {
  if (value == null || value === "") return "json";
  const normalized = String(value).toLowerCase();
  return AXIOS_RESPONSE_TYPES.has(normalized) ? normalized : "json";
};

const useCallAxios = ({ context, responseType: hookResponseType = "json" }) => {
  const { addToast } = useToast();
  const isMountedRef = React.useRef(true);
  const cancelSourcesRef = React.useRef([]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelSourcesRef.current.forEach((source) => {
        source.cancel("Component unmounted");
      });
      cancelSourcesRef.current = [];
    };
  }, []);

  const [requestLoading, setRequestLoading] = React.useState({
    ...defaultMethods,
  });
  const [requestMessage, setRequestMessage] = React.useState({
    ...defaultMethods,
  });
  const [requestSuccess, setRequestSuccess] = React.useState({
    ...defaultMethods,
  });
  const [requestError, setRequestError] = React.useState({
    ...defaultMethods,
  });

  const [requestData, setRequestData] = React.useState({});
  const { validateError } = useValidateErrorResponse();

  const fetchAPI = ({
    verb,
    context,
    responseType = hookResponseType,
    queryParameters = null,
    id = null,
    afterID = null,
    body = null,
    noRefreshData,
    headers = {},
    showToast
  }) => {
    // Replace in-flight GET (account switch must not keep the previous account's saldo).
    if (verb === "GET" && cancelSourcesRef.current.length) {
      cancelSourcesRef.current.forEach((source) => {
        source.cancel("replaced");
      });
      cancelSourcesRef.current = [];
    }

    if (verb === "GET" || !requestLoading[verb]) {
      setRequestMessage({ ...requestMessage, [verb]: null });
      setRequestError({ ...defaultMethods, [verb]: null });
      setRequestLoading({ ...defaultMethods, [verb]: true });
      setRequestSuccess({
        GET: null,
        POST: null,
        PUT: null,
        PATCH: null,
        DELETE: null,
      });

      const axiosToken = CancelToken.source();
      const cancelToken = axiosToken?.token;
      cancelSourcesRef.current.push(axiosToken);

      const removeCancelSource = () => {
        cancelSourcesRef.current = cancelSourcesRef.current.filter(
          (source) => source !== axiosToken
        );
      };

      return new Promise((resolve, reject) => {
        fetchAxios({
          cancelToken,
          verb,
          responseType: resolveAxiosResponseType(responseType),
          context,
          queryParameters,
          id,
          afterID,
          body,
          additionalHeaders: headers
        })
          .then((values) => {
            removeCancelSource();
            if (!isMountedRef.current) {
              resolve();
              return;
            }
            setRequestError({ ...defaultMethods });
            setRequestMessage({ ...defaultMethods });
            setRequestLoading((prev) => ({ ...prev, [verb]: false }));
            setRequestSuccess((prev) => ({ ...prev, [verb]: true }));

            if (!noRefreshData) {
              setRequestData(values);
            }

            resolve();
          })
          .catch((err) => {
            removeCancelSource();
            if (axios.isCancel(err) || !isMountedRef.current) {
              resolve(null);
              return;
            }
            if (showToast && !!err.response?.data)
              addToast({
                title: err.response.data.information,
                description: err.response.data.description,
                type: err.response.data.isError ? "error" : "warning",
              });
            setRequestError((prev) => ({ ...prev, [verb]: true }));
            setRequestSuccess((prev) => ({ ...prev, [verb]: false }));
            setRequestMessage((prev) => ({
              ...prev,
              [verb]: err.response?.data,
            }));
            setRequestLoading((prev) => ({ ...prev, [verb]: false }));
            if (!noRefreshData) {
              setRequestData({});
            }
            validateError(err?.response);
            resolve(null);
          });
      });
    }
  };

  const fetchGET = ({
    queryParameters = {},
    id = null,
    afterID = null,
    responseType = hookResponseType,
    showToast = true,
  }) => {
    const verb = "GET";
    return fetchAPI({
      verb,
      responseType,
      context,
      queryParameters,
      id,
      afterID,
      showToast
    });
  };

  const fetchPATCH = ({
    queryParameters = {},
    id = null,
    body = {},
    noRefreshData = false,
    showToast = true,
  }) => {
    const verb = "PATCH";
    return fetchAPI({
      verb,
      context,
      queryParameters,
      id,
      body,
      noRefreshData,
      showToast,
    });
  };

  const fetchPOST = ({
    queryParameters = {},
    id = null,
    body = {},
    noRefreshData = false,
    headers = {},
    showToast = true,
  }) => {
    const verb = "POST";
    return fetchAPI({
      verb,
      context,
      queryParameters,
      id,
      body,
      noRefreshData,
      headers,
      showToast,
    });
  };

  const fetchPUT = ({
    queryParameters = {},
    context,
    id = null,
    body = {},
    noRefreshData = false,
    showToast = true,
  }) => {
    const verb = "PUT";
    return fetchAPI({
      verb,
      context,
      queryParameters,
      id,
      body,
      noRefreshData,
      showToast,
    });
  };

  const fetchDELETE = ({
    queryParameters = {},
    id = null,
    body = {},
    noRefreshData,
    showToast = true,
  }) => {
    const verb = "DELETE";
    return fetchAPI({
      verb,
      context,
      queryParameters,
      id,
      body,
      noRefreshData,
      showToast,
    });
  };

  const reset = () => {
    setRequestData({ ...defaultMethods });
    setRequestMessage({ ...defaultMethods });
    setRequestError({ ...defaultMethods });
    setRequestLoading({ ...defaultMethods });
    setRequestSuccess({ ...defaultMethods });
  };

  return {
    requestLoading,
    requestData,
    requestError,
    requestMessage,
    requestSuccess,
    fetchDELETE,
    fetchGET,
    fetchPATCH,
    fetchPOST,
    fetchPUT,
    reset,
  };
};

export default useCallAxios;
